// Imports
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { darken, lighten, saturate } from 'polished';
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import AnchorButton from '../../../../../../shareables/foundation/front-end/components/buttons/anchor';
import { useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import memberHasPrivilege from '../../../../../../shareables/utils/member-has-privilege';
import { GeneratedThemeProps } from '../../../../../templates/template-style';


// Styled component
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	color: ${(props) => darken(0.2, props.theme.baseColor)};
	border-bottom: 0.0625rem solid ${(props) => saturate(0.2, lighten(0.5, props.theme.baseColor))};
	margin-bottom: 0.9375rem;
	padding-bottom: 0.3125rem;
	
	grid-column: ${(props) => (props.theme.isFlipped ? 2 : 1)};
	
	display: flex;
	justify-content: space-between;
	align-items: center;
	
	${(props) =>
		props.theme.isPaper &&
		css`
			border-bottom: 0.0625rem solid ${lighten(0.8, props.theme.baseColor)};
		`}
`;

const HeaderText = styled.h1`
	font-size: 1.5625rem;
	line-height: 1.5625rem;
	font-weight: 500;
	overflow-wrap: anywhere;
	
	&& a,
	&& a:hover,
	&& a:focus {
		color: inherit;
		text-decoration: none;
	}
`;

const EditPage = styled(AnchorButton)`
	padding: 0.0625rem 0.375rem;
	font-size: 0.75rem;
	line-height: 1.5;
	border-radius: 0.1875rem;
	background-color: #337ab7;
	margin-left: 1rem;
	margin-top: 0.25rem;
	height: 1.4rem;
	text-decoration: none !important;
	
	&:hover {
		background-color: #286090 !important;
	}
	
	.svg-inline--fa {
		margin-right: 0.25rem;
		font-size: 0.625rem;
	}
`;


// Map page type IDs to page type routes for Admin Portal
const pageType: Record<number, string> = {
	1: 'homepage',
	200: 'general-text',
	201: 'documents',
	202: 'calendar',
	203: 'board-members',
	204: 'news-articles',
	205: 'location',
	206: 'photo-gallery',
	207: 'resident-directory',
	208: 'pet-directory',
	209: 'committees',
	210: 'contact-form',
	211: 'faqs',
	212: 'garage-sales',
	213: 'recipes',
	214: 'community-facilities',
	215: 'forum',
	216: 'classifieds',
	217: 'useful-contact-information',
	218: 'custom-form',
	219: 'resident-alerts',
	220: 'survey-results',
};


// Define the accepted props
interface Props extends React.PropsWithChildren {
	editPageDetails?: {
		pageTypeID: number;
		pageID: number;
	};
	linkTo?: string;
}


// Function component
const Heading = React.forwardRef<HTMLHeadingElement, Props>(({ editPageDetails, linkTo, children }, ref) => {
	// Use Redux functionality
	const websiteID = useAppSelector((state) => state.self.website?.id);
	const memberPrivileges = useAppSelector((state) => state.self.member?.privileges);
	const adminView = useAppSelector((state) => state.self.adminView);
	
	
	// Determine if user is a Page Admin
	const isPageAdmin = memberHasPrivilege('PAGES', memberPrivileges ?? []);
	
	
	// Return JSX
	return (
		<Container>
			<HeaderText ref={ref}>{linkTo ? <Link to={linkTo}>{children}</Link> : children}</HeaderText>
			
			{editPageDetails && adminView && (
				<EditPage
					href={`https://admin.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/${isPageAdmin ? 'pages' : 'page-editor'}/${pageType[editPageDetails.pageTypeID]}/${editPageDetails.pageID}/view?websiteID=${websiteID ?? ''}`}
				>
					<FontAwesomeIcon icon={faPencil} />
					Edit page
				</EditPage>
			)}
		</Container>
	);
});


// @ts-expect-error: displayName does exist
Heading.displayName = 'Heading';

export default Heading;
